import React, { useState, useEffect } from "react";
import { MultiSelect } from "components/SportSelect/MultiSelect";
import { selectCurrentClinician } from "store/ClinicianStore/selectors";
import { useAppSelector } from "hooks/reduxHooks";
import { POSITIONS } from "constants/positions";
import { getGroupComparison } from "repositories/patientApi";
import { getTeamsString } from "repositories/teamsApi";
import Grid from "@mui/material/Grid";
import { AthleteModal } from "./AthletesTable";

const letters = ["A", "A", "B", "C", "D", "E", "F", "G"];

interface Group {
  _id: number;
  teams: Array<any>; // or Team[] if you have a Team type
  sports: Array<any>; // or Sport[] if you have a Sport type
  genders: Array<any>; // or Gender[] if you have a Gender type
  positions: Array<any>; // or Position[] if you have a Position type
  average: string;
  previousAverage: string;
  athletes: {
    patient: {
      profileInfo: {
        firstName: string;
        lastName: string;
        teams: string[];
      };
    };
  }[];
}

const Row = ({
  label,
  children,
  showlabels = false,
}: {
  label: any;
  children: React.ReactNode;
  showlabels?: boolean;
}) => (
  <div
    style={{
      display: "flex",
      padding: "16px 24px", // Added horizontal padding
      borderBottom: "1px solid #e5e7eb",
      alignItems: "center",
      gap: "16px", // Added gap between label and content
    }}
  >
    {showlabels && (
      <div
        style={{
          color: "#6b7280",
          fontWeight: 500,
          width: "120px", // Fixed width for labels
          flexShrink: 0, // Prevent label from shrinking
        }}
      >
        {label}
      </div>
    )}
    {children}
  </div>
);

const GroupComparison = ({
  teams,
  groups = [],
  setGroups,
}: {
  teams: any[];
  groups: Group[];
  setGroups: (groups: Group[]) => void;
}) => {
  const [hoveredGroup, setHoveredGroup] = useState<number | null>(null);
  const [refetchCount, setRefresCount] = useState<number>(0);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const currentClinician: any = useAppSelector(selectCurrentClinician);

  const headerStyle: React.CSSProperties = {
    display: "flex",
    gap: "24px", // Increased gap between columns
    padding: "16px 0",
    overflowX: "auto",
    borderBottom: "1px solid #e5e7eb",
    position: "relative" as const, // For "Add Group" button positioning
  };

  const handleChange = (groupId: string, field: string, value: string) => {
    setHasSearched(true);
    const prevGroups = groups;
    const newGroups = prevGroups.map((group) => {
      if (group._id === parseInt(groupId)) {
        // Handle array fields
        if (Array.isArray(group[field as keyof Group])) {
          const currentArray = group[field as keyof Group] as any[];
          const valueExists = currentArray.includes(value);

          return {
            ...group,
            [field]: valueExists
              ? currentArray.filter((item) => item !== value) // Remove if exists
              : [...currentArray, value], // Add if doesn't exist
          };
        }
        // Handle non-array fields
        return {
          ...group,
          [field]: value,
        };
      }
      return group;
    });
    setGroups(newGroups);

    setRefresCount((prev) => prev + 1);
  };

  const fetchGroupsData = async () => {
    const parsedGroups = groups.map((group) => {
      return {
        ...group,
        teams: group.teams.map((team) => {
          return teams.find((t) => t.name === team)?._id;
        }),
      };
    });
    const response = await getGroupComparison({ groups: parsedGroups });
    const reponseParsedGroups = response.groups.map((responseGroup: any) => {
      const currentGroup = groups.find(
        (group) => group._id === responseGroup._id
      );

      return {
        ...responseGroup,
        teams: currentGroup?.teams,
      };
    });
    setGroups(reponseParsedGroups);
  };

  useEffect(() => {
    if (hasSearched) {
      fetchGroupsData();
    }
  }, [refetchCount]);

  const deleteGroup = (_id: number) => {
    // First filter out the group with the specified _id
    const filteredGroups = groups.filter((group) => group._id !== _id);

    // Then reindex the remaining groups
    const updateGroups = filteredGroups.map((group, index) => ({
      ...group,
      _id: index + 1,
    }));
    setGroups(updateGroups);
  };

  const SelectField = ({
    label,
    value,
    options = [],
    style = {},
    groupId,
  }: {
    options: string[];
    label: string;
    value: any;
    groupId: string;
    style?: React.CSSProperties;
  }) => {
    return (
      <div
        style={{
          backgroundColor: "#f3f4f6",
          padding: "8px 12px",
          borderRadius: "6px",
          display: "flex",
          width: "250px",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          ...style,
        }}
      >
        <MultiSelect
          options={options}
          selectedOptions={value}
          customRenderValue={value.join(", ")}
          paddingTop={0}
          hint={label}
          onItemSelected={(e: any, value: any) => {
            handleChange(groupId, label?.toLowerCase(), value);
          }}
          menuTitle={""}
          style={{
            border: "1px solid #E5E5E5",
            height: "34px",
            borderRadius: "6px",
            backgroundColor: "#f3f4f6",
            fontSize: "14px",
            color: "#374151",
            width: "100%",
          }}
          emptyMessage=""
        />
      </div>
    );
  };

  const getModalList = (group: Group) => {
    const list = group.athletes.map((athlete, i) => {
      return {
        ...athlete,
        patient: {
          ...athlete.patient,
          profileInfo: {
            ...athlete.patient.profileInfo,
            teamsString:
              getTeamsString(athlete.patient.profileInfo.teams, teams) ||
              "Team not assigned",
          },
        },
      };
    });
    const sortedList = list.sort(
      (a: any, b: any) =>
        (a.calculations.currentAverage || 0) -
        (b.calculations.currentAverage || 0)
    );
    return sortedList.map((athlete: any, i: number) => {
      return {
        ...athlete,
        patient: {
          ...athlete.patient,
          rank: i + 1,
        },
      };
    });
  };

  return (
    <div style={{}}>
      {/* Add Group Button */}
      <button
        onClick={() => {
          setGroups([
            ...groups,
            {
              _id: groups.length + 1,
              teams: [],
              sports: [],
              positions: [],
              athletes: [],
              genders: [],
              average: "",
              previousAverage: "",
            },
          ]);
        }}
        style={{
          position: "relative",
          top: "50px",
          backgroundColor: "#e5e7eb",
          padding: "8px 16px",
          zIndex: "9",
          borderRadius: "6px",
          border: "none",
          cursor: "pointer",
          color: "#374151",
          fontWeight: 500,
        }}
      >
        Add Group
      </button>

      <div style={headerStyle}>
        {groups.map((group, i) => (
          <div
            onMouseEnter={() => setHoveredGroup(group._id)}
            onMouseLeave={() => setHoveredGroup(null)}
            key={group._id}
            style={{
              display: "flex",
              flexDirection: "column",
              marginLeft: "0", // Offset first column to account for "Add Group" button
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px 24px",
                backgroundColor:
                  group._id === hoveredGroup && i !== 0
                    ? "#1f2937"
                    : "transparent",
                borderRadius: "6px",
                color:
                  group._id === hoveredGroup && i !== 0 ? "white" : "black",
                fontWeight: 500,
                marginBottom: "16px",
                paddingLeft: i === 0 ? "40%" : "24px",
              }}
            >
              Group {letters[group._id]}
              {group._id === hoveredGroup && i !== 0 && (
                <span
                  onClick={() => {
                    deleteGroup(group._id);
                  }}
                  style={{ marginLeft: "20px", cursor: "pointer" }}
                >
                  ✕
                </span>
              )}
            </div>

            <div style={i === 0 ? {} : { maxWidth: "350px", width: "100%" }}>
              <Row showlabels={i === 0} label="Sport">
                <SelectField
                  groupId={group._id.toString()}
                  value={group.sports}
                  options={currentClinician?.profileInfo?.sports}
                  label="Sports"
                />
              </Row>

              <Row showlabels={i === 0} label="Team">
                <SelectField
                  groupId={group._id.toString()}
                  options={teams.map((item) => item.name)}
                  value={group.teams}
                  label="Teams"
                />
              </Row>

              <Row showlabels={i === 0} label="Position">
                <SelectField
                  groupId={group._id.toString()}
                  options={POSITIONS}
                  value={group.positions}
                  label="Positions"
                />
              </Row>

              <Row showlabels={i === 0} label="Gender">
                <SelectField
                  groupId={group._id.toString()}
                  options={["Male", "Female", "Other"]}
                  value={group.genders}
                  label="Genders"
                />
              </Row>

              <Row showlabels={i === 0} label="Athletes">
                <a
                  href="#"
                  style={{
                    color: "#3b82f6",
                    textDecoration: "none",
                    padding: "8px 0",
                  }}
                >
                  <Grid
                    md={12}
                    xl={12}
                    sx={{
                      width: "100%",
                      textAlign: "center",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    <AthleteModal
                      customTrigger={<>Athlete full list</>}
                      athletes={getModalList(group)}
                    />
                  </Grid>
                  {/*  </Tooltip> */}
                </a>
              </Row>

              <Row
                showlabels={i === 0}
                label={
                  <div>
                    <div>Min Avg</div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#9ca3af",
                        marginTop: "4px",
                      }}
                    >
                      (last & current exam)
                    </div>
                  </div>
                }
              >
                <div
                  style={{
                    color: "#6b7280",
                    padding: "8px 0",
                  }}
                >
                  {i === 0 ? (
                    <>
                      {!group.sports.length &&
                      !group.genders.length &&
                      !group.positions.length &&
                      !group.teams.length
                        ? "N/A"
                        : group.average}
                    </>
                  ) : (
                    <>{group.average || "N/A"}</>
                  )}
                </div>
              </Row>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GroupComparison;
